import type {
  Profile,
  ProfileComponent,
  ProfileComponentConfig,
  ProfileComponentType,
  ProfileFontStyle,
  ProfileStatus,
  ProfileThemeConfig,
  SocialPlatform,
} from '@nx/domain';
import {
  DEFAULT_THEME_CONFIG,
  normalizeProfileOptionalText,
  normalizeProfileRoleType,
} from '@nx/domain';
import type { DocumentSnapshot } from 'firebase/firestore';
import { toDate } from '../event/firestoreMappers';

function mapThemeConfig(data: Record<string, unknown> | undefined): ProfileThemeConfig {
  const raw = (data ?? {}) as Record<string, unknown>;
  return {
    templateId: String(raw.templateId ?? DEFAULT_THEME_CONFIG.templateId),
    primaryColor: String(raw.primaryColor ?? DEFAULT_THEME_CONFIG.primaryColor),
    backgroundColor: (raw.backgroundColor as string | undefined) ?? null,
    backgroundImageUrl: (raw.backgroundImageUrl as string | undefined) ?? null,
    fontStyle: (raw.fontStyle as ProfileFontStyle) ?? DEFAULT_THEME_CONFIG.fontStyle,
    buttonRadius:
      typeof raw.buttonRadius === 'number' ? raw.buttonRadius : DEFAULT_THEME_CONFIG.buttonRadius,
    buttonColor: String(raw.buttonColor ?? DEFAULT_THEME_CONFIG.buttonColor),
  };
}

export function mapProfileDoc(id: string, data: Record<string, unknown>): Profile {
  return {
    id,
    ownerId: String(data.ownerId ?? ''),
    slug: String(data.slug ?? ''),
    title: String(data.title ?? ''),
    description: (data.description as string | undefined) ?? null,
    tagline: normalizeProfileOptionalText(data.tagline as string | undefined),
    location: normalizeProfileOptionalText(data.location as string | undefined),
    avatarUrl: (data.avatarUrl as string | undefined) ?? null,
    roleType: normalizeProfileRoleType(data.roleType),
    tags: Array.isArray(data.tags) ? (data.tags as string[]) : [],
    status: (data.status as ProfileStatus) ?? 'draft',
    themeConfig: mapThemeConfig(data.themeConfig as Record<string, unknown> | undefined),
    viewCount: typeof data.viewCount === 'number' ? data.viewCount : 0,
    createdAt: toDate(data.createdAt),
    updatedAt: toDate(data.updatedAt),
  };
}

// Missing fields must be omitted (not set to undefined): the mapped config flows
// back into updateDoc(), and Firestore rejects undefined field values.
function mapComponentConfig(raw: Record<string, unknown>): ProfileComponentConfig {
  const config: ProfileComponentConfig = {};
  if (raw.platform != null) config.platform = raw.platform as SocialPlatform;
  if (raw.url != null) config.url = raw.url as string;
  if (raw.label != null) config.label = raw.label as string;
  if (raw.content != null) config.content = raw.content as string;
  if (raw.imageUrl != null) config.imageUrl = raw.imageUrl as string;
  if (raw.embedId != null) config.embedId = raw.embedId as string;
  if (raw.handle != null) config.handle = raw.handle as string;
  if (raw.note != null) config.note = raw.note as string;
  if (raw.caption != null) config.caption = raw.caption as string;
  return config;
}

export function mapProfileComponentDoc(
  profileId: string,
  id: string,
  data: Record<string, unknown>,
): ProfileComponent {
  return {
    id,
    profileId,
    type: (data.type as ProfileComponentType) ?? 'custom_link',
    config: mapComponentConfig((data.config as Record<string, unknown> | undefined) ?? {}),
    visible: data.visible !== false,
    sortOrder: typeof data.sortOrder === 'number' ? data.sortOrder : 0,
    clickCount: typeof data.clickCount === 'number' ? data.clickCount : 0,
    createdAt: toDate(data.createdAt),
  };
}

export function snapshotData(snapshot: DocumentSnapshot): Record<string, unknown> {
  return snapshot.data() as Record<string, unknown>;
}
